const FALLBACK_PREFIX = '/manifest-engine';

/**
 * Returns the configured Insights microservice base URL, or undefined if unset.
 */
export function getInsightsApiUrl(): string | undefined {
  const envUrl = import.meta.env.VITE_INSIGHTS_API_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    return envUrl.trim().replace(/\/$/, '');
  }
  return undefined;
}

/**
 * Builds clean headers for requests to the Insights microservice.
 * Strictly omits X-Requested-With and X-XSRF-TOKEN to prevent CORS failures.
 */
export function getInsightsHeaders(
  accept = 'application/json',
  contentType?: string,
  extraHeaders?: Record<string, string>
): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    Accept: accept,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(contentType ? { 'Content-Type': contentType } : {}),
    ...extraHeaders
  };

  delete headers['X-Requested-With'];
  delete headers['X-XSRF-TOKEN'];

  return headers;
}

/**
 * RequestInit for fetch() calls to Insights (e.g. SSE streaming or binary download).
 * Uses credentials: 'omit' to prevent CORS wildcard conflicts.
 */
export function getInsightsFetchInit(
  method: string,
  accept: string,
  body?: BodyInit | null,
  extraHeaders?: Record<string, string>
): RequestInit {
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const contentType = isFormData ? undefined : body ? 'application/json' : undefined;

  return {
    method,
    credentials: 'omit',
    headers: getInsightsHeaders(accept, contentType, extraHeaders),
    ...(body != null ? { body } : {})
  };
}

/**
 * Resolves endpoint path: returns direct microservice URL if VITE_INSIGHTS_API_URL
 * is configured, or falls back to /manifest-engine prefixed path.
 */
export function getInsightsApiPath(suffix: string): string {
  const normalized = suffix.startsWith('/') ? suffix : `/${suffix}`;
  const directUrl = getInsightsApiUrl();
  if (directUrl) {
    return `${directUrl}${normalized}`;
  }
  return `${FALLBACK_PREFIX}${normalized}`;
}

/**
 * Resolves absolute or full URL for fetch() calls.
 */
export function getInsightsUrl(suffix: string, fallbackBase?: string): string {
  const normalized = suffix.startsWith('/') ? suffix : `/${suffix}`;
  const directUrl = getInsightsApiUrl();
  if (directUrl) {
    return `${directUrl}${normalized}`;
  }
  const base = (fallbackBase ?? '').replace(/\/$/, '');
  return `${base}${FALLBACK_PREFIX}${normalized}`;
}
